import React, { useMemo } from "react";

/**
 * Draftwave-branded default avatar.
 *
 * Draws a deterministic animated "constellation face" — a stylized character
 * built from an orbit ring, a starburst core, and two glinting eyes — with
 * colors and rotation seeded from the user's email or id. Rendered as inline
 * SVG so it scales cleanly and keeps the brand aesthetic across the app.
 */

function hashString(s) {
  let h = 2166136261;
  const str = String(s || "draftwave");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

// Palette pairs (bg, ring, glow, accent) — all on-brand.
const PALETTES = [
  ["#0F1020", "#FF5A3C", "#F4B860", "#F9F5EC"], // Draftwave core (dark + coral)
  ["#111827", "#22D3EE", "#A78BFA", "#F9F5EC"], // aurora
  ["#0B132B", "#F97316", "#FDE68A", "#FFFFFF"], // sunset
  ["#1E1B4B", "#F472B6", "#60A5FA", "#F9F5EC"], // nebula
  ["#052E2B", "#34D399", "#FDE68A", "#F9F5EC"], // emerald
  ["#1A0B2E", "#F43F5E", "#F59E0B", "#F9F5EC"], // magenta
  ["#0A0A1F", "#93C5FD", "#F0ABFC", "#F9F5EC"], // ice + orchid
  ["#12122A", "#FDBA74", "#38BDF8", "#F9F5EC"], // dusk
];

export default function DefaultAvatar({
  seed = "draftwave",
  size = 80,
  animated = true,
  className = "",
  title,
}) {
  const cfg = useMemo(() => {
    const h = hashString(seed);
    const pal = PALETTES[h % PALETTES.length];
    return {
      bg: pal[0],
      ring: pal[1],
      glow: pal[2],
      accent: pal[3],
      rot: (h % 360),
      // subtle variations so no two look identical
      eyeOffset: ((h >> 3) % 8) - 4,
      starPoints: 5 + ((h >> 5) % 4), // 5..8 points
      spin: 18 + ((h >> 7) % 14), // 18-32s per rev
      // small "twinkle" delay so a group of avatars don't pulse in sync
      twinkleDelay: ((h >> 9) % 1000) / 1000,
    };
  }, [seed]);

  const uid = useMemo(() => `av-${hashString(seed).toString(36)}`, [seed]);

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title || "Draftwave user avatar"}
      className={`select-none ${className}`}
      data-testid="default-avatar"
    >
      <defs>
        <radialGradient id={`${uid}-bg`} cx="50%" cy="45%" r="65%">
          <stop offset="0%" stopColor={cfg.glow} stopOpacity="0.6" />
          <stop offset="55%" stopColor={cfg.bg} stopOpacity="1" />
          <stop offset="100%" stopColor="#000" stopOpacity="1" />
        </radialGradient>
        <radialGradient id={`${uid}-core`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={cfg.accent} stopOpacity="1" />
          <stop offset="60%" stopColor={cfg.ring} stopOpacity="0.95" />
          <stop offset="100%" stopColor={cfg.ring} stopOpacity="0.15" />
        </radialGradient>
      </defs>

      {/* deep-space disc */}
      <circle cx="50" cy="50" r="50" fill={`url(#${uid}-bg)`} />

      {/* tiny background stars */}
      <g fill={cfg.accent} opacity="0.55">
        <circle cx="18" cy="24" r="0.9" />
        <circle cx="82" cy="30" r="0.6" />
        <circle cx="26" cy="76" r="0.7" />
        <circle cx="74" cy="72" r="0.9" />
        <circle cx="60" cy="14" r="0.5" />
      </g>

      {/* orbit ring (slow, animated) */}
      <g
        transform={`rotate(${cfg.rot} 50 50)`}
        style={
          animated
            ? {
                transformOrigin: "50% 50%",
                animation: `dwSpin ${cfg.spin}s linear infinite`,
              }
            : undefined
        }
      >
        <ellipse
          cx="50"
          cy="50"
          rx="34"
          ry="14"
          fill="none"
          stroke={cfg.ring}
          strokeOpacity="0.85"
          strokeWidth="1.4"
        />
        <circle
          cx="84"
          cy="50"
          r="2.4"
          fill={cfg.ring}
          style={
            animated
              ? {
                  animation: `dwTwinkle 2.6s ease-in-out infinite`,
                  animationDelay: `${cfg.twinkleDelay}s`,
                }
              : undefined
          }
        />
      </g>

      {/* central starburst core */}
      <g
        transform="translate(50 50)"
        style={
          animated
            ? {
                transformOrigin: "50px 50px",
                animation: `dwPulse 3.6s ease-in-out infinite`,
              }
            : undefined
        }
      >
        <Starburst points={cfg.starPoints} outer={16} inner={6} fill={`url(#${uid}-core)`} />
        <circle cx="0" cy="0" r="4.5" fill={cfg.accent} />
      </g>

      {/* two glinting "eyes" — the friendly character bit */}
      <g fill={cfg.accent}>
        <circle cx={40 + cfg.eyeOffset} cy="44" r="1.9" />
        <circle cx={60 + cfg.eyeOffset} cy="44" r="1.9" />
      </g>

      {/* subtle rim highlight */}
      <circle cx="50" cy="50" r="49" fill="none" stroke={cfg.ring} strokeOpacity="0.35" strokeWidth="0.8" />

      {/* Keyframes scoped once — safe to inline multiple times, browser dedupes */}
      <style>{`
        @keyframes dwSpin { to { transform: rotate(360deg); } }
        @keyframes dwPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.08); opacity: 0.92; }
        }
        @keyframes dwTwinkle {
          0%, 100% { opacity: 0.4; r: 2.2; }
          50% { opacity: 1; r: 3.2; }
        }
      `}</style>
    </svg>
  );
}

function Starburst({ points = 6, outer = 16, inner = 6, fill }) {
  const pts = [];
  const total = points * 2;
  for (let i = 0; i < total; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const angle = (Math.PI * 2 * i) / total - Math.PI / 2;
    pts.push(`${(Math.cos(angle) * r).toFixed(2)},${(Math.sin(angle) * r).toFixed(2)}`);
  }
  return <polygon points={pts.join(" ")} fill={fill} />;
}
